<template>
   <main>
      <Head>
         <title>{{ product.meta_title }}</title>
         <meta
            name="description"
            :content="product.meta_description"
            inertia="description"
         />
         <link
            rel="canonical"
            :href="url('product', cleanSlug)"
         />
         <link
            v-if="mainImageWebp"
            rel="preload"
            :href="mainImageWebp"
            as="image"
            type="image/webp"
            fetchpriority="high"
         />
         <link
            v-if="logo"
            rel="preload"
            :href="logo"
            as="image"
            fetchpriority="high"
         />
         <link
            v-if="product.brand?.image"
            rel="preload"
            :href="product.brand.image"
            as="image"
         />
      </Head>

      <div class="bg-white/30 w-full pb-8">
         <!-- Admin edit links -->
         <div
            class="flex justify-end relative max-w-(--breakpoint-2xl) mx-auto space-x-2"
            v-if="user && user.is_admin && [30, 25, 33595].includes(user.id)"
         >
            <div class="flex items-center space-x-2 absolute right-8 top-3">
               <a
                  target="_blank"
                  :href="`https://nv.doorax.nl/nova-beheer/resources/products/${product.id}/edit`"
                  class="text-xs text-gray-500 hover:text-gray-700 border border-orange-500 rounded-md px-2 py-1"
               >
                  <fa
                     :icon="['fal', 'pencil']"
                     class="h-3 w-3"
                  />
                  Nova
               </a>
               <a
                  target="_blank"
                  :href="`https://content.doorax.nl/product-find/${product.id}`"
                  class="text-xs text-gray-500 hover:text-gray-700 border border-blue-500 rounded-md px-2 py-1"
               >
                  <fa
                     :icon="['fal', 'pencil']"
                     class="h-3 w-3"
                  />
                  Content
               </a>
            </div>
         </div>

         <div class="max-w-(--breakpoint-2xl) container mx-auto px-4 lg:px-8 pt-4 pb-8">
            <!-- Breadcrumbs -->
            <Breadcrumbs />

            <div
               v-if="showResource"
               class="bg-red-500/10 text-red-500 text-center my-4 py-2 rounded-md mx-auto max-w-2xl"
            >
               Product is uitgeschakeld.
            </div>

            <div class="flex flex-wrap">
               <div class="w-full md:w-1/3 md:pr-6 pb-6 relative">
                  <div class="z-10 relative mb-4">
                     <!-- Title -->
                     <h1 class="text-lg font-semibold mb-2">{{ product.title ?? product.name }}</h1>

                     <!-- Rating -->
                     <div
                        class="flex items-center"
                        v-if="product.reviews.count"
                     >
                        <review-stars :score="product.reviews.score" />

                        <span
                           class="ml-2 text-sm text-gray-600 link"
                           @click="$scrollTo('#product-reviews', 500)"
                           >{{ product.reviews.score }}/5 ({{ product.reviews.count }} {{ $t('beoordelingen') }})</span
                        >
                     </div>
                  </div>

                  <img
                     :src="logo"
                     class="absolute z-10 right-6 top-6 max-w-16"
                     :name="product.brand.name"
                  />

                  <!-- ProductImages async + lazy -->
                  <LazyHydrate when-visible>
                     <ProductImages
                        :images="imagesRaw"
                        :product="product"
                        @update:images="updateImage"
                        :color="predefined.color"
                     />
                  </LazyHydrate>

                  <transition name="fade">
                     <div
                        class="mt-8 sticky top-10 z-20 bg-white"
                        v-if="setImage && showBox"
                     >
                        <Transition name="fade">
                           <fa
                              :icon="['fad', 'spinner-third']"
                              class="z-50 text-dgreen opacity-80 fa-spin mx-auto w-8 h-8 absolute left-1/2 -ml-4 top-1/2 -mt-8"
                              v-if="setImageLoading"
                           />
                        </Transition>
                        <div class="justify-center mt-4 pb-8 rounded-xl border-2 border-dgreen/30 shadow-xl overflow-hidden">
                           <div class="p-4">
                              <img
                                 :src="setImage"
                                 class="max-h-[700px] mx-auto"
                                 loading="lazy"
                              />
                           </div>
                        </div>
                        <transition name="fade">
                           <div
                              v-if="setImageLoading"
                              class="z-40 inset-0 bg-white/50 absolute transition-all"
                           ></div>
                        </transition>
                        <div class="-mt-8 z-50 relative">
                           <img
                              :src="asset('img/open-box.png')"
                              class="w-32 -mt-6 -mr-6 float-right"
                              loading="lazy"
                           />
                        </div>
                        <span
                           class="items-center bg-white border border-dgreen/50 shadow w-fit float-right rounded-l-lg text-center mx-auto block py-1 px-3 text-xs font-bold mt-3 -mr-12 z-10 pr-12"
                        >
                           <span>Jouw unieke samengestelde set</span>
                        </span>
                     </div>
                  </transition>

                  <div class="relative group mt-8 md:mt-0">
                     <div
                        class="md:p-6 bg-white md:my-6 md:w-full rounded-lg border border-dgreen/40 relative overflow-hidden group-hover:border-dgreen/60 transition-all"
                        v-if="product.show_gelijksluitend || (product.reorder_type != 2 && product.skg)"
                     >
                        <div
                           class="hidden md:flex mr-2 items-center md:mb-4"
                           v-if="product.show_gelijksluitend"
                        >
                           <img
                              class="inline-block"
                              alt="gelijksluitend"
                              title="gelijksluitend"
                              fetchpriority="high"
                              width="32"
                              height="22"
                              :src="asset('img/gelijksluitend.png')"
                              loading="lazy"
                           />
                           <span class="inline-block leading-4 text-sm mx-3">
                              <info-modal
                                 :infoIcon="true"
                                 title="Gelijksluitend geleverd"
                                 body="Gelijksluitende cilinders zijn cilinders die met dezelfde sleutel geopend kunnen worden. Zo kunnen zowel de voor als achterdeur met één sleutel geopend worden. Dit scheelt onnodige sleutels aan de sleutelbos."
                                 >Gelijksluitend geleverd.</info-modal
                              >
                           </span>
                        </div>

                        <div
                           class="hidden md:flex mr-2 items-center md:mb-4"
                           v-if="product.reorder_type != 2 && product.skg"
                        >
                           <img
                              class="h-5 inline-block"
                              v-if="product.skg == 2 && product.reorder_type != 2"
                              :src="asset('img/skg-2.png')"
                              alt="SKG2 keurmerk"
                              fetchpriority="high"
                              width="27"
                              height="20"
                              title="Deze cilinder is voorzien van het SKG2 keurmerk."
                              loading="lazy"
                           />
                           <div class="block w-10 h-auto">
                              <SkgIcon
                                 :skg="product.skg"
                                 class="w-fit"
                              />
                           </div>
                           <span class="inline-block leading-4 text-sm mx-3">
                              <InfoModal
                                 :infoIcon="true"
                                 title="SKG 3* keurmerk"
                                 link="/api/blocks/skg3"
                                 >Voorzien van het SKG{{ product.skg }} keurmerk.</InfoModal
                              >
                           </span>
                        </div>

                        <div
                           class="p-4 md:p-0 md:my-0 flex mr-2 items-center"
                           v-if="product.lowest_price_guarantee"
                        >
                           <div class="w-7 justify-center flex">
                              <fa
                                 :icon="['fal', 'money-check-dollar']"
                                 class="h-6 w-6 text-gray-600"
                              />
                           </div>
                           <span class="inline-block leading-4 text-sm mx-3">
                              <info-modal
                                 :infoIcon="true"
                                 title="Laagsteprijsgarantie"
                                 link="/api/blocks/laagsteprijsgarantie"
                                 >Laagsteprijsgarantie.</info-modal
                              >
                           </span>
                        </div>

                        <logo-icon
                           color="#1560A4"
                           class="hidden md:block absolute top-1/2 -translate-y-1/2 -right-4 h-20 opacity-10"
                        />
                     </div>
                  </div>
               </div>

               <!-- Right -->
               <div
                  class="w-full md:w-2/3 relative"
                  ref="configurator"
               >
                  <div
                     v-if="product.id == 4"
                     class="mb-5 text-gray-600"
                  >
                     <h2 class="text-xl font-bold">M&C Color Pro nabestellen</h2>
                     Helaas is dit type cilinder niet meer leverbaar omdat de fabrikant in 2018 gestopt is met de productie hiervan. Heeft u
                     een van de onderstaande certificaten dan kunnen wij in samenwerking met M&C een vervangende set aanbieden.
                     <div class="my-6 flex flex-wrap w-full">
                        <div class="px-4 w-1/3">
                           <img
                              class="w-full"
                              :src="asset('img/pr77.png')"
                              loading="lazy"
                           />
                        </div>
                        <div class="px-4 w-1/3">
                           <img
                              class="w-full"
                              :src="asset('img/pr08.png')"
                              loading="lazy"
                           />
                        </div>
                        <div class="px-4 w-1/3">
                           <img
                              class="w-full"
                              :src="asset('img/pr99.png')"
                              loading="lazy"
                           />
                        </div>
                     </div>
                     Gebruik onderstaand formulier om een vrijblijvende aanbieding voor een vervangende set aan te vragen. Vul hier in
                     hoeveel cilinders en sleutels u in totaal nodig heeft. Als laatste stap vragen wij om het certificaat van de huidige
                     M&C Color Pro set te uploaden zodat we dit als bewijs aan kunnen leveren bij M&C. Zonder een foto van het certificaat
                     kunnen we geen aanbieding maken.
                  </div>

                  <div
                     class="bg-white md:rounded-xl shadow-overallsm transition -mx-4 md:mx-0 relative"
                     id="config"
                  >
                     <cylinder-configurator
                        :product="product"
                        :cylinders="cylinders"
                        :item="item"
                        :filter="filter"
                        :options="options"
                        :predefined="predefined"
                        :text_blocks="text_blocks"
                        :sizesB="sizesB"
                        :keys="keys"
                        :steps="steps"
                        @loading="loading = true"
                        @endLoading="loading = false"
                        @setImageLoading="setImageLoading = true"
                        @endImageLoading="setImageLoading = false"
                        @watcherActivated="watcherActivated = true"
                     />

                     <!-- Totals -------------------------------------------->
                     <div
                        class="step p-6 relative bg-white rounded-b-xl overflow-hidden transition-all flex flex-wrap"
                        :class="[stepsCompleted ? 'border-t-2 border-dgreen' : 'border-t']"
                        v-if="product.id == 4"
                     >
                        <div class="relative">
                           <div
                              v-if="!stepsCompleted"
                              class="absolute inset-0 bg-white opacity-50 w-full h-full"
                           ></div>
                           <button
                              class="button green"
                              @click="showQuote"
                           >
                              Offerte aanvragen
                           </button>
                        </div>
                        <div class="my-2">
                           Op werkdagen sturen we binnen 24 uur een passende aanbieding. Spoed? Neem contact met ons op.
                        </div>
                     </div>

                     <div
                        v-else
                        class="step p-6 relative bg-white rounded-b-xl overflow-hidden transition-all flex flex-wrap"
                        :class="[stepsCompleted ? 'border-t-2 border-dgreen' : 'border-t']"
                     >
                        <!-- Totals -->
                        <div class="w-full md:w-1/2 md:pr-6 md:border-r border-gray-200 space-y-3 relative">
                           <div class="md:pl-12 mb-6">
                              <transition name="fade">
                                 <fa
                                    :icon="['fad', 'spinner-third']"
                                    class="text-dgreen opacity-50 fa-spin mx-auto w-5 h-5 absolute right-6 top-2"
                                    v-if="loading"
                                 />
                              </transition>

                              <!-- Todo: steps_completed -->
                              <fa
                                 :icon="[stepsCompleted ? 'fas' : 'fal', 'tags']"
                                 class="hidden md:block fa-flip-horizontal left-0 mt-1 absolute text-xl"
                                 :class="[stepsCompleted ? 'text-dgreen' : 'text-gray-400']"
                              />

                              <!-- Cylinders -->
                              <strong class="mb-4 ml-6 md:ml-0 block">Overzicht</strong>

                              <Totals
                                 :cylinders="form.cylinders"
                                 :padlocks="form.padlocks"
                                 :rimlocks="form.rimlocks"
                                 :totals="configurationTotals"
                                 :date="deliveryDate"
                                 :keys="form.keys"
                                 class="leading-6"
                                 :product="product"
                                 :attributes="{}"
                              />
                              <div v-if="!watcherActivated">
                                 <li class="flex flex-wrap">
                                    <div class="w-1/2">Totaal incl. BTW</div>
                                    <div class="w-1/2 text-right">
                                       <span class="font-extrabold text-lg">{{ price(product.price) }}</span>
                                    </div>
                                 </li>
                              </div>

                              <!-- Prijs -->
                              <div
                                 class="w-full border-t mt-2 pt-2"
                                 v-if="user && (user.is_admin || impersonated)"
                              >
                                 <label
                                    for="price"
                                    class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                    >Aangepaste prijs incl. BTW (optioneel)</label
                                 >
                                 <div class="relative mt-2 rounded-md shadow">
                                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                       <span class="text-gray-500 sm:text-sm">€</span>
                                    </div>
                                    <input
                                       type="number"
                                       name="price"
                                       id="price"
                                       class="block w-full rounded-md border-0 py-1.5 pl-8 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                       placeholder="0,00"
                                       v-model="form.customPrice"
                                    />
                                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                       <!-- <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span> -->
                                    </div>
                                 </div>

                                 <!-- Comments for intern usage -->
                                 <div class="mt-3">
                                    <label
                                       for="website"
                                       class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                       >Opmerkingen (intern gebruik)</label
                                    >

                                    <textarea
                                       type="text"
                                       name="product_comment"
                                       id="product_comment"
                                       placeholder="Opmerkingen"
                                       v-model="form.comment"
                                       rows="3"
                                       class="text-input relative bg-transparent inline-block"
                                    />
                                 </div>
                              </div>

                              <div
                                 class="relative my-8"
                                 v-if="setImage"
                              >
                                 <transition name="fade">
                                    <fa
                                       :icon="['fad', 'spinner-third']"
                                       class="z-50 text-dgreen opacity-80 fa-spin mx-auto w-8 h-8 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
                                       v-if="loading"
                                    />
                                 </transition>
                                 <div class="justify-center mt-4 pb-2 rounded-xl border-2 border-dgreen/30 overflow-hidden">
                                    <div class="px-4">
                                       <div class="text-sm my-3 text-gray-700">
                                          <span
                                             >Jouw unieke samengestelde <strong>{{ product.model }}</strong> set</span
                                          >
                                       </div>
                                       <img
                                          :src="setImage"
                                          class="max-h-[450px] mx-auto"
                                          loading="lazy"
                                       />
                                    </div>
                                 </div>
                                 <transition name="fade">
                                    <div
                                       v-if="setImageLoading"
                                       class="z-40 inset-0 bg-white/50 absolute transition-all"
                                    ></div>
                                 </transition>
                              </div>
                           </div>
                        </div>
                        <div class="w-full md:w-1/2">
                           <Transition name="pop">
                              <div
                                 class="mt-2"
                                 v-if="configurationTotals && Object.values(configurationTotals.upsell).length"
                              >
                                 <div class="md:px-6 mb-4">
                                    <strong class="block mb-4">Aanbevolen accessoires</strong>
                                    <div
                                       class="rounded-lg hover:bg-gray-50 border cursor-pointer relative items-center mb-2"
                                       v-for="value in configurationTotals.upsell"
                                       :class="{
                                          'bg-dgreen2/5 border-dgreen2': form.upsellIds.includes(value.id),
                                       }"
                                       :key="value.id"
                                    >
                                       <label
                                          :for="value.id"
                                          class="flex items-center px-4 py-2 cursor-pointer md:text-sm"
                                       >
                                          <input
                                             type="checkbox"
                                             :id="value.id"
                                             :value="value.id"
                                             v-model="form.upsellIds"
                                             class="h-5 w-5 border-gray-300 rounded text-dgreen3 transition focus:ring-0 mr-6"
                                          />
                                          <vue-picture
                                             :image="value.image"
                                             :style="'h-8'"
                                             class="mr-4"
                                          />
                                          <span class="grow">
                                             <strong class="inline-block">{{ value.value }}</strong>

                                             <info-modal
                                                :title="value.value"
                                                :body="value.description"
                                                :infoIcon="true"
                                             >
                                             </info-modal>
                                          </span>
                                          <span
                                             v-if="value.price"
                                             class="ml-2"
                                             >{{ price(value.price) }}</span
                                          >
                                       </label>
                                    </div>
                                 </div>
                              </div>
                           </Transition>

                           <div class="pt-4 md:pt-0 md:pl-6">
                              <div
                                 class="w-full border-b mb-4 pb-4"
                                 v-if="user && user.business_account"
                              >
                                 <label
                                    for="website"
                                    class="block text-sm font-semibold mb-2 leading-6 text-gray-900"
                                    >Referentie <span class="font-normal text-gray-600">(optioneel)</span></label
                                 >

                                 <input
                                    type="text"
                                    name="reference"
                                    id="reference"
                                    class="block w-full rounded-md border-0 py-1.5 pl-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    placeholder="Referentie"
                                    v-model="form.reference"
                                 />
                              </div>

                              <date
                                 class="mb-4"
                                 :date="deliveryDate"
                                 :pickup="product.delivery_days == 1 ? firstPickupDate : false"
                              />

                              <warning
                                 v-if="configurationTotals.late_delivery"
                                 :warning="configurationTotals.late_delivery_description"
                                 class="mb-4"
                              />

                              <!-- Actions -->
                              <form
                                 @submit.prevent="submit"
                                 class=""
                                 ref="totals"
                              >
                                 <button
                                    type="submit"
                                    :disabled="form.processing"
                                    class="button green w-full mb-4"
                                 >  
                                    <fa
                                       :icon="['fad', 'spinner-third']"
                                       class="text-white opacity-80 mr-2 fa-spin"
                                       v-if="form.processing && !redirectToCheckout"
                                    />
                                    <fa
                                       v-else
                                       :icon="['fal', 'shopping-cart']"
                                       class="text-white mr-2 h-5 w-5"
                                    />
                                    In winkelwagen
                                 </button>

                                 <div
                                    v-if="hasErrors && Object.keys(form.errors).length"
                                    class="mb-4"
                                 >
                                    <div class="bg-red-50 px-4 py-3 text-red-900 rounded-lg relative text-sm error">
                                       <div class="flex">
                                          <ul>
                                             <div
                                                v-for="error in Object.values(form.errors)"
                                                :key="error"
                                             >
                                                <span
                                                   class="block"
                                                   v-if="error"
                                                >
                                                   • {{ error }}
                                                </span>
                                             </div>
                                          </ul>
                                       </div>
                                    </div>
                                 </div>
                              </form>

                              <div class="flex flex-wrap">
                                 <div class="pr-4 w-1/2">
                                    <button
                                       class="button default w-full group"
                                       @click="showShareModal()"
                                    >
                                       <fa
                                          :icon="['fal', 'envelope']"
                                          class="h-5 w-5 text-gray-400 mr-2 group-hover:text-gray-500 transition"
                                       />Delen
                                    </button>
                                 </div>

                                 <div class="w-1/2">
                                    <button
                                       class="button semi-green default w-full group"
                                       @click="handleSubmitCheckout()"
                                    >
                                       <fa
                                          :icon="['fad', 'spinner-third']"
                                          class="text-gray-400 opacity-80 mr-2 fa-spin"
                                          v-if="form.processing && redirectToCheckout"
                                       />
                                       <fa
                                          v-else
                                          :icon="['fal', 'credit-card']"
                                          class="h-5 w-5 text-green-600 mr-2 group-hover:text-green-700 transition"
                                       />
                                       Nu bestellen
                                    </button>
                                 </div>
                              </div>

                              <usps
                                 class="mt-4"
                                 :returnable="product.returnable"
                              />
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <!-- Product details -->
         </div>
      </div>

      <div class="w-full bg-white">
         <!-- ProductInfo async + lazy -->
         <LazyHydrate when-visible>
            <ProductInfo
               :product="product"
               :options="options"
               :specifications="specifications"
               :versionimages="versionimages"
               :colorImages="colorImages"
               :techimages="techimages"
               :text_blocks="text_blocks"
               :cylinderQty="form.cylinders ? form.cylinders.length : 1"
               :keyQty="configurationTotals.default_key_qty ?? 0"
               :keys_table="keys_table"
            />
         </LazyHydrate>
      </div>

      <!-- ShareProduct async -->
      <ShareProduct
         ref="share"
         :product="product"
         :form="form"
      />
      <!-- Todo Summary nav for mobile -->
      <transition name="slide-up">
         <!-- SummaryNav async + lazy -->
         <LazyHydrate when-visible>
            <SummaryNav
               :product="product"
               :totals="configurationTotals"
               :loading="form.processing"
               :image="images[0]"
               v-show="showSummaryNav"
               class="md:hidden bottom-0 z-20 left-0 fixed w-full py-3 bg-white shadow-overallxl"
               @submit="submit"
            />
         </LazyHydrate>
      </transition>

      <transition name="slide-up">
         <LazyHydrate when-visible>
            <SummaryNav
               :product="product"
               :totals="configurationTotals"
               :loading="form.processing"
               :image="images[0]"
               v-show="isScrolled && !configuratorIsVisible && product.id != 4 && !isSSR"
               class="hidden md:block bottom-0 z-20 left-0 fixed w-full py-1 bg-white shadow-overallxl"
               @submit="submit"
            />
         </LazyHydrate>
      </transition>

      <div v-html="schemaOrg"></div>
      <div
         v-if="schemaOrgFAQ"
         v-html="schemaOrgFAQ"
      ></div>
      <teleport to="#modals">
         <modal
            title=""
            ref="quote"
         >
            <strong>Offerte aanvragen</strong>
            <template v-slot:content>
               <!-- ColorProQuoteRequest async + lazy -->
               <LazyHydrate when-visible>
                  <ColorProQuoteRequest
                     @close="hideQuote"
                     :formData="form"
                  />
               </LazyHydrate>
            </template>
         </modal>
      </teleport>
   </main>
</template>

<script setup>
import Usps from '@/Blocks/Usps.vue'
import Totals from '@/Components/Configuration/Legacy/Totals.vue'
// Async import optimalisaties
import { defineAsyncComponent } from 'vue'
const ProductImages = defineAsyncComponent(() => import('@/Components/ProductImagesSplide.vue'))
const ProductInfo = defineAsyncComponent(() => import('@/Components/ProductInfo.vue'))
const ShareProduct = defineAsyncComponent(() => import('@/Components/ShareProduct.vue'))
const ColorProQuoteRequest = defineAsyncComponent(() => import('@/Components/ColorProQuoteRequest.vue'))
const SummaryNav = defineAsyncComponent(() => import('@/Components/Configuration/Legacy/SummaryNav.vue'))
// Einde async imports
import { useCartStore } from '@/stores/cartStore.js'
import { usePage } from '@inertiajs/vue3'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useElementVisibility } from '@vueuse/core'
const cartStore = useCartStore()
// Configuration components
import LogoIcon from '@/Blocks/LogoIcon.vue'
import CylinderConfigurator from '@/Components/Configuration/Legacy/CylinderConfigurator.vue'
import useIsMobile from '@/Composables/useIsMobile'
const isMobile = useIsMobile()
// Shared components
import Warning from '@/Components/Warning.vue'
import { router } from '@inertiajs/vue3'
import { toRaw } from 'vue'
import SkgIcon from '../../Blocks/SkgIcon.vue'
const inBrowser = typeof window !== 'undefined'
const configurationTotals = ref(false)
const watcherActivated = ref(false)
const props = defineProps({
   product: Object,
   images: Array,
   expectedDate: Object,
   logo: String,
   cylinders: Array,
   specifications: Object,
   versionimages: Object,
   colorImages: Object,
   techimages: Object,
   text_blocks: Object,
   keys_table: [String, Boolean],
   options: Object,
   item: [Object, Boolean],
   filter: Boolean,
   keys: [Number, Boolean],
   predefined: Object,
   user: Object,
   popup: Boolean,
   firstPickupDate: Object,
   schemaOrg: String,
   schemaOrgFAQ: [String, Boolean],
   sizesB: Object,
   steps: Object,
   showResource: Boolean,
})

onMounted(() => {
   if (inBrowser) {
      function handleUpdateForm(data) {
         form.value = data.form
         configurationTotals.value = data.totals
      }

      window.mitt.on('updateForm', handleUpdateForm)

      window.mitt.on('updateSetImage', (data) => {
         setImageLoading.value = false
         configurationTotals.value.set_image = data.image
         setImage.value = data.image
      })

      let gtag = usePage().props.flash.gtag
      if (gtag) {
         let parsedGtag = JSON.parse(JSON.stringify(gtag))
         dataLayer.push({ ecommerce: null })
         dataLayer.push(parsedGtag)
      }

      if (props.user && props.user.is_admin) {
         document.addEventListener('keydown', (e) => {
            if (e.code == 'KeyS') {
               showBox.value = !showBox.value
            }
         })
      }

      shiftChatIcon(false)
      window.addEventListener('scroll', handleScroll)
   }

   if (props.item) {
      setTimeout(() => {
         document.getElementById('config').scrollIntoView({ behavior: 'smooth' })
      }, 200)
   }
})

// Ref components
const quote = ref(false)
const share = ref(false)
const imagesRaw = ref(props.images)
// Default values
const showWizard = ref(false)
const popupCounter = ref(0)
const form = ref({})
const showBox = ref(false)
const setImage = ref(false)
// Loaders
const setImageLoading = ref(false)
const loading = ref(false)
const redirectToCheckout = ref(false)
// Summary nav
const totals = ref(null)
const totalsIsVisible = useElementVisibility(totals)

const configurator = ref(null)
const configuratorIsVisible = useElementVisibility(configurator)

const isScrolled = ref(false)
const scrollTimeout = ref(null)

const cleanSlug = computed(() => {
   const [path] = props.product.slug.split('?')
   return path
})

const deliveryDate = computed(() => {
   let customDate = configurationTotals.value ? configurationTotals.value.late_delivery : false
   return customDate ? customDate : props.expectedDate
})

const impersonated = computed(() => {
   return usePage().props.impersonated_by
})

function updateImage(images) {
   imagesRaw.value = images
}

const hasErrors = computed(() => {
   let errors = usePage().props.errors
   let count = 0
   Object.values(errors).forEach((v) => {
      if (v) {
         count++
      }
   })
   return count
})

const stepsCompleted = computed(() => {
   if (props.product.reorder_type) {
      if (form.value.keysSelected && form.value.file) {
         return true
      }
   } else {
      if (form.value.keysSelected) {
         return true
      }
   }

   return false
})

// Debounced scroll handler function
function handleScroll() {
   if (scrollTimeout.value) {
      clearTimeout(scrollTimeout.value)
   }

   scrollTimeout.value = setTimeout(() => {
      isScrolled.value = window.scrollY > 100
   }, 100)
}

const mainImageWebp = computed(() => {
   return props.images[0].src
})

const isSSR = computed(() => {
   return !inBrowser
})

function logError(errors) {
   if (errors) {
      let events = {
         qtySelected: 'validatie_product_aantal',
         cylindersValid: 'validatie_product_opties',
         keysSelected: 'validatie_product_sleutels',
         upload: 'validatie_product_upload',
      }

      let key = Object.keys(errors)[0]

      dataLayer.push({ event: events[key] })

      if (typeof hj !== 'undefined') {
         hj('event', events[key])
      }
   }
}

function showShareModal() {
   share.value.showModal()
}

function showQuote() {
   quote.value.open = true
}

function hideQuote() {
   quote.value.open = false
}

function handleSubmitCheckout() {
   redirectToCheckout.value = true
   submit()
}

const showSummaryNav = computed(() => {
   return isScrolled.value && !totalsIsVisible.value && props.product.id != 4 && isMobile.value
})

watch(showSummaryNav, (newVal) => {
   if (newVal) {
      document.body.classList.add('modal-open');
   } else {
      document.body.classList.remove('modal-open');
   }
})

function submit() {
   if (!form.value.cylinders) {
      const cylinders = document.getElementById('config')
      if (cylinders) {
         cylinders.scrollIntoView({ behavior: 'smooth' })
      }
      return
   }

   form.value
      .transform((data) => ({
         ...data,
         item: props.item ? props.item.id : false,
         cylinders: form.value.cylinders,
         customPrice: form.value.customPrice,
         comment: form.value.comment,
         reference: form.value.reference,
         keys: form.value.keys,
         set_image: configurationTotals.value.set_image,
         calculated: configurationTotals.value.set_image_data?.calculated,
      }))
      .post('/winkelwagen', {
         preserveScroll: (page) => Object.keys(page.props.errors).length,
         onError(errors) {
            // Scroll to error
            var errorsArray = document.getElementsByClassName('error-step')
            var firstError = errorsArray[0]
            firstError.scrollIntoView({ behavior: 'smooth' })
            logError(errors)
         },
         onSuccess() {
            cartStore.fetchCartData()

            let gtag = toRaw(usePage().props.flash.gtag)

            if (redirectToCheckout.value) {
               if (gtag) {
                  let parsedGtag = JSON.parse(JSON.stringify(gtag))
                  dataLayer.push(parsedGtag)

                  // Push add_to_cart event
                  let cartGtag = JSON.parse(JSON.stringify(gtag))
                  cartGtag.event = 'add_to_cart'
                  dataLayer.push(cartGtag)

                  // Push checkout event
                  let checkoutGtag = JSON.parse(JSON.stringify(gtag))
                  checkoutGtag.event = 'add_to_checkout'
                  dataLayer.push(checkoutGtag)
               }

               router.visit('/checkout')
            } else {
               if (gtag) {
                  // Push checkout event
                  let checkoutGtag = JSON.parse(JSON.stringify(gtag))
                  checkoutGtag.event = 'add_to_cart_normal'
                  dataLayer.push(checkoutGtag)
               }

               window.mitt.emit('openCartSlide', true)
            }
         },
      })
}

const showPopup = computed(() => {
   if (inBrowser) {
      return props.popup == 0 ? true : false
   }
   return false
})

function shiftChatIcon(value = true) {
   if (inBrowser) {
      const chat = document.getElementById('widget-fake-mobile')
      if (chat) {
         chat.classList.toggle('mb-16', !value)
      }
   }
}

// Add cleanup for scroll event listener
onUnmounted(() => {
   if (inBrowser) {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeout.value) {
         clearTimeout(scrollTimeout.value)
      }
   }
})

// Dynamic import of image component based on environment
// const ProductImages = defineAsyncComponent(() =>
//     inBrowser ? import('@/Components/ProductImagesSplide.vue') : import('@/Components/ProductImagesSSR.vue')
// );
</script>
